import { hasWorkingDay } from '@nest-recruit/shared';

/**
 * Calendars A, "Year at a glance": the twelve months of one year, built from the calendar's
 * `workingDays` bitmask (bit 0 = Sunday, the same as `getUTCDay`) and its holidays. Dates are
 * plain `YYYY-MM-DD` days in the calendar's own zone, never instants, so no zone can shift a day.
 */

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

export interface YearDay {
  readonly iso: string;
  readonly day: number;
  /** 0 = Sunday … 6 = Saturday. */
  readonly weekday: number;
}

export interface YearMonth {
  readonly index: number;
  readonly name: string;
  /** Empty cells before the 1st, in a Sunday-first week. */
  readonly leadingBlanks: number;
  readonly days: readonly YearDay[];
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function isoDay(year: number, monthIndex: number, day: number): string {
  return `${year}-${pad(monthIndex + 1)}-${pad(day)}`;
}

export function monthsOf(year: number): readonly YearMonth[] {
  return MONTH_NAMES.map((name, index) => {
    const length = new Date(Date.UTC(year, index + 1, 0)).getUTCDate();
    const days = Array.from({ length }, (_, offset): YearDay => {
      const day = offset + 1;
      return { iso: isoDay(year, index, day), day, weekday: new Date(Date.UTC(year, index, day)).getUTCDay() };
    });
    return { index, name, leadingBlanks: days[0]?.weekday ?? 0, days };
  });
}

export function isWorkingWeekday(workingDays: number, weekday: number): boolean {
  return hasWorkingDay(workingDays, weekday);
}

export interface YearTotals {
  readonly working: number;
  readonly holidays: number;
  readonly daysOff: number;
}

/**
 * "245 working days · 16 holidays · 104 days off". A holiday on a day that is off anyway is counted
 * once, as a holiday, so the three always add up to the days in the year. Holidays from another year
 * are not counted.
 */
export function yearTotals(year: number, workingDays: number, holidayDates: ReadonlySet<string>): YearTotals {
  let working = 0;
  let holidays = 0;
  let daysOff = 0;
  for (const month of monthsOf(year)) {
    for (const day of month.days) {
      if (holidayDates.has(day.iso)) holidays += 1;
      else if (isWorkingWeekday(workingDays, day.weekday)) working += 1;
      else daysOff += 1;
    }
  }
  return { working, holidays, daysOff };
}

/** Today's date where the calendar is (its own zone), as `YYYY-MM-DD`. */
export function todayIn(timeZone: string, now: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** The date `days` from `iso`, for moving through the year with the arrow keys. */
export function shiftDay(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const moved = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  return isoDay(moved.getUTCFullYear(), moved.getUTCMonth(), moved.getUTCDate());
}

/**
 * The years the switch offers: this year and next, so planning ahead is never stuck, the year on
 * screen, and the older years that have holidays go in the picker beside it.
 */
export function yearChoices(thisYear: number, shown: number, withHolidays: readonly number[]): { readonly recent: readonly number[]; readonly other: readonly number[] } {
  const recent = [thisYear, thisYear + 1];
  const all = new Set<number>([...withHolidays, shown]);
  return { recent, other: [...all].filter((year) => !recent.includes(year)).sort((a, b) => b - a) };
}

/**
 * The holiday list under the year: in the current year, the ones already past fold into "+N earlier"
 * so what is coming is what shows; any other year shows whole.
 */
export function splitEarlier<T extends { readonly holidayDate: string }>(holidays: readonly T[], year: number, today: string): { readonly earlier: readonly T[]; readonly coming: readonly T[] } {
  if (Number(today.slice(0, 4)) !== year) return { earlier: [], coming: holidays };
  return { earlier: holidays.filter((holiday) => holiday.holidayDate < today), coming: holidays.filter((holiday) => holiday.holidayDate >= today) };
}
